import React, { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Shield, Save, Check, X, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { useNotification } from '../contexts/NotificationContext';

/**
 * RoleManagement (Edit Role) — standalone page to edit an existing role's name & permissions.
 * Props:
 *   role        — the role object to edit (required: role_id, name, permissions[])
 *   onSuccess() — called after save succeeds
 *   onCancel()  — called when Cancel is clicked
 *   initialOpenAdd — legacy prop kept for backward compatibility (ignored here)
 */
const RoleManagement = ({ role, onSuccess, onCancel, initialOpenAdd }) => {
    const { userRole } = useContext(AuthContext);
    const { showNotification } = useNotification();

    const [formData, setFormData] = useState({
        name: role?.name || '',
        description: role?.description || '',
        permissions: Array.isArray(role?.permissions) ? [...role.permissions] : []
    });
    const [isSaving, setIsSaving] = useState(false);

    // ── Permission structure ─────────────────────────────────────────────────
    const permissionStructure = [
        {
            category: 'DASHBOARD',
            subsections: [{ name: 'DASHBOARD', key: 'Dashboard', actions: ['View'] }]
        },
        {
            category: 'USER & ACCESS MANAGEMENT',
            subsections: [
                { name: 'USERS', key: 'Users', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'ROLES', key: 'Roles', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'PATIENT MANAGEMENT',
            subsections: [
                { name: 'PATIENTS', key: 'Patients', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PATIENT FEEDBACK', key: 'Patient Feedback', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'PRE-OPERATIVE',
            subsections: [
                { name: 'OT BOOKING', key: 'OT Booking', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PRE-OP CHECKLIST', key: 'Pre-Op Checklist', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PRE-OP ORDERS', key: 'Pre-Op Orders', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'PRE-ANAESTHETIC EVALUATION', key: 'Pre-Anaesthetic Evaluation', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'CONSENTS', key: 'Consents', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'INTRA-OPERATIVE',
            subsections: [
                { name: 'OPERATION SHEET', key: 'Operation Sheet', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'NURSING RECORD', key: 'Nursing Record', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'SURGICAL SAFETY', key: 'Surgical Safety Checklist', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'POST-OPERATIVE',
            subsections: [
                { name: 'POST-OP ORDERS', key: 'Post-Op Orders', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'POST-OP HANDOVER', key: 'Post-Op Handover', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'ALDERETE SCORE', key: 'Alderete Score', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'POST-OP INSTRUCTIONS', key: 'Post-Op Instructions', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        },
        {
            category: 'INVENTORY & SETTINGS',
            subsections: [
                { name: 'INVENTORY', key: 'Inventory', actions: ['View', 'Add', 'Edit', 'Delete'] },
                { name: 'OT ROOMS', key: 'OT Rooms', actions: ['View', 'Add', 'Edit', 'Delete'] }
            ]
        }
    ];

    const flatPermissions = [];
    permissionStructure.forEach(cat => {
        cat.subsections.forEach(sub => {
            sub.actions.forEach(action => {
                flatPermissions.push(`${sub.key} ${action}`);
            });
        });
    });

    // ── Permission helpers ───────────────────────────────────────────────────
    const togglePermission = (permKey) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permKey)
                ? prev.permissions.filter(p => p !== permKey)
                : [...prev.permissions, permKey]
        }));
    };

    const handleQuickAction = (actionType) => {
        setFormData(prev => {
            let newPerms = [...prev.permissions];
            if (actionType === 'Check All') {
                newPerms = [...flatPermissions];
            } else if (actionType === 'Uncheck All') {
                newPerms = [];
            } else {
                const allOfSuffix = flatPermissions.filter(p => p.endsWith(actionType));
                const allSelected = allOfSuffix.every(p => prev.permissions.includes(p));
                if (allSelected) {
                    newPerms = newPerms.filter(p => !p.endsWith(actionType));
                } else {
                    allOfSuffix.forEach(p => { if (!newPerms.includes(p)) newPerms.push(p); });
                }
            }
            return { ...prev, permissions: newPerms };
        });
    };

    // ── Validation ───────────────────────────────────────────────────────────
    const validate = () => {
        if (!formData.name.trim()) {
            showNotification('error', 'Role name cannot be empty.');
            return false;
        }
        if (formData.name.trim().length < 3) {
            showNotification('error', 'Role name must be at least 3 characters.');
            return false;
        }
        return true;
    };

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!validate()) return;
        if (!role?.role_id) {
            showNotification('error', 'No role selected to edit.');
            return;
        }

        setIsSaving(true);
        const payload = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            permissions: formData.permissions
        };

        try {
            await axios.put(`${API_BASE_URL}/update_role/${role.role_id}`, payload, { withCredentials: true });
            showNotification('success', `Role "${payload.name}" updated successfully!`);
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error updating role:', error);
            const msg = error.response?.data?.message || 'Server error.';
            showNotification('error', `Failed to update role: ${msg}`);
        } finally {
            setIsSaving(false);
        }
    };

    const actionColors = {
        View: { color: '#10b981', bg: '#f0fdf4', border: '#a7f3d0' },
        Add: { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
        Edit: { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
        Delete: { color: '#ef4444', bg: '#fef2f2', border: '#fecaca' }
    };

    // If no role provided, show a placeholder
    if (!role) {
        return (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                <Shield style={{ width: 48, height: 48, color: '#e2e8f0', margin: '0 auto 16px', display: 'block' }} />
                <p style={{ margin: 0, fontWeight: 600 }}>No role selected.</p>
                <p style={{ margin: '8px 0 0' }}>Go to Role List and click Edit on a role.</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', minHeight: '100vh', background: '#f8fafc' }}>

            {/* Page Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                {onCancel && (
                    <button
                        onClick={onCancel}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: '#f8fafc', color: '#64748b',
                            border: '1px solid #e2e8f0', borderRadius: '10px',
                            padding: '9px 14px', fontSize: '13px', fontWeight: 600,
                            cursor: 'pointer', transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                        onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                    >
                        <ChevronLeft style={{ width: 15, height: 15 }} /> Back to List
                    </button>
                )}
                <div>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>Edit Role</h2>
                    <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#94a3b8' }}>
                        Editing: <strong style={{ color: '#10b981' }}>{role.name}</strong>
                    </p>
                </div>
            </div>

            {/* Top Form Card */}
            <div style={{
                background: '#fff', borderRadius: '16px',
                border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                padding: '24px', marginBottom: '20px'
            }}>
                {/* Save button row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                        <strong style={{ color: '#10b981', fontSize: '16px' }}>{formData.permissions.length}</strong> permissions selected
                    </span>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: isSaving ? '#6ee7b7' : '#10b981',
                            color: '#fff', border: 'none', borderRadius: '10px',
                            padding: '10px 22px', fontSize: '14px', fontWeight: 700,
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 14px rgba(16,185,129,0.2)',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => { if (!isSaving) e.currentTarget.style.background = '#059669'; }}
                        onMouseLeave={e => { if (!isSaving) e.currentTarget.style.background = '#10b981'; }}
                    >
                        <Save style={{ width: 16, height: 16 }} />
                        {isSaving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>

                {/* Role name input */}
                <div style={{ marginBottom: '20px', maxWidth: '400px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
                        ROLE NAME <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                        <Shield style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8' }} />
                        <input
                            type="text"
                            value={formData.name}
                            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            style={{
                                width: '100%', padding: '12px 14px 12px 42px',
                                border: '1.5px solid #e2e8f0', borderRadius: '12px',
                                fontSize: '14px', fontWeight: 600, color: '#0f172a',
                                background: '#fff', outline: 'none', boxSizing: 'border-box',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={e => e.target.style.borderColor = '#10b981'}
                            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>
                </div>

                {/* Quick actions */}
                <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                        QUICK ACTIONS:
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {['View', 'Add', 'Edit', 'Delete'].map(action => (
                            <button
                                key={action}
                                onClick={() => handleQuickAction(action)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                    background: actionColors[action].bg, color: actionColors[action].color,
                                    border: `1px solid ${actionColors[action].border}`,
                                    borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.15s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                                <Check style={{ width: 13, height: 13 }} /> All {action}
                            </button>
                        ))}
                        <div style={{ width: '1px', background: '#e2e8f0', margin: '0 4px', alignSelf: 'stretch' }} />
                        <button
                            onClick={() => handleQuickAction('Check All')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                background: '#f0fdf4', color: '#10b981', border: '1px solid #a7f3d0',
                                borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.15s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                            <Check style={{ width: 13, height: 13 }} /> Check All
                        </button>
                        <button
                            onClick={() => handleQuickAction('Uncheck All')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px', fontSize: '13px', fontWeight: 700,
                                background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0',
                                borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.15s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                            <X style={{ width: 13, height: 13 }} /> Uncheck All
                        </button>
                    </div>
                </div>
            </div>

            {/* Permission Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '32px' }}>
                {permissionStructure.map((category) => (
                    <div
                        key={category.category}
                        style={{
                            background: '#fff', borderRadius: '16px',
                            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            overflow: 'hidden'
                        }}
                    >
                        <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                            <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 800, color: '#1e293b', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                                {category.category}
                            </h4>
                        </div>
                        <div>
                            {category.subsections.map((sub, subIdx) => (
                                <div
                                    key={sub.name}
                                    style={{
                                        padding: '16px 20px',
                                        borderBottom: subIdx < category.subsections.length - 1 ? '1px solid #f8fafc' : 'none',
                                        display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap'
                                    }}
                                >
                                    <span style={{
                                        fontSize: '11px', fontWeight: 700, color: '#10b981',
                                        letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: '200px'
                                    }}>
                                        {sub.name}
                                    </span>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                        {sub.actions.map((action) => {
                                            const permKey = `${sub.key} ${action}`;
                                            const isChecked = formData.permissions.includes(permKey);
                                            const ac = actionColors[action] || actionColors.View;
                                            return (
                                                <label key={action} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                                                    <div
                                                        onClick={() => togglePermission(permKey)}
                                                        style={{
                                                            width: '20px', height: '20px', borderRadius: '6px',
                                                            border: `2px solid ${isChecked ? ac.color : '#d1d5db'}`,
                                                            background: isChecked ? ac.color : '#fff',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            transition: 'all 0.15s', flexShrink: 0
                                                        }}
                                                    >
                                                        {isChecked && <Check style={{ width: 12, height: 12, color: '#fff', strokeWidth: 3 }} />}
                                                    </div>
                                                    <span style={{
                                                        fontSize: '13px', fontWeight: isChecked ? 700 : 500,
                                                        color: isChecked ? '#1e293b' : '#94a3b8', transition: 'color 0.15s'
                                                    }}>
                                                        {action}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RoleManagement;
